import {
  DropdownButton,
  DropdownButtonType,
  List,
  ListItem,
  TabActivationMode,
  TabAndPanelContents,
  TabsWidget
} from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { Messages } from '../../codegen/Localize';
import { AuthorizableTypeSelectorStyles } from '../styles/AuthorizableTypeSelectorStyle';
import { AuthorizableType, getAuthorizableTypeLabel } from '../types/AuthorizableType';

export const PERMISSIONS_TABS_TESTID = 'permissions-dialog-tabs';
export const PERMISSIONS_TABS_OVERFLOW_TESTID = 'permissions-dialog-tabs-overflow';
export const PERMISSIONS_TABS_OVERFLOW_POPUP_ID = 'permissions-dialog-tabs-overflow-popup';
export const MAX_AUTHORIZABLE_TYPE_TABS: number = 6;

export interface AuthorizableTypeSelectorProps {
  totalAuthorizableTypes: AuthorizableType[];
  onSelectAuthorizableType: (index: number) => void;
}
export type AuthorizableTypeSelectorState = {
  /** If the last tab should be used as a dropdown */
  displayDropdown: boolean;
  /** If the last tab should say "More" as the dropdown label, otherwise it will display the selected overflow authorizable type */
  displayMore: boolean;
  previousSelectedTab: number;
  selectedDropdownValue: string;
};

/**
 * The Permission Dialog for a "container" authorizable type (such as projects) allows users to set permissions for child authorizable types of that container.
 * This component is for selecting an authorizable type.
 * 
 * TFSID 1372812: there are too many authorizable types in projects for all of them to fit nicely in the dialog. This will switch over to using the "Tabs Overflow"
 * Widget once that is available, but in the meantime UX has called for "hacking" together the overflow experience with the existing Tabs. Currently the
 * Permission Dialog in general is not part of any accessibility audit, so any minor accessibility issues with this temporary UX were deprioritied.
 */
export class AuthorizableTypeSelector extends React.Component<AuthorizableTypeSelectorProps, AuthorizableTypeSelectorState> {
  private authorizableTypesForTabs: AuthorizableType[] = null;
  private authorizableTypesForDropdown: AuthorizableType[] = null;
  private tabsRef: React.RefObject<TabsWidget> = React.createRef<TabsWidget>();
  private buttonRef: React.RefObject<DropdownButton> = React.createRef<DropdownButton>();
  private closeDropdown: () => void = null;

  constructor(props: AuthorizableTypeSelectorProps) {
    super(props);
    this.setOriginalTabs();
    this.setDropdownValues();
    this.state = {
      displayDropdown: props.totalAuthorizableTypes.length > MAX_AUTHORIZABLE_TYPE_TABS,
      displayMore: props.totalAuthorizableTypes.length > MAX_AUTHORIZABLE_TYPE_TABS,
      previousSelectedTab: 0,
      selectedDropdownValue: null,
    };
  }

  private setOriginalTabs = (): void => {
    this.authorizableTypesForTabs = this.props.totalAuthorizableTypes.slice();
    if (this.props.totalAuthorizableTypes.length > MAX_AUTHORIZABLE_TYPE_TABS) {
      this.authorizableTypesForTabs = this.props.totalAuthorizableTypes.slice(0, MAX_AUTHORIZABLE_TYPE_TABS - 1);
    }
  };

  private setDropdownValues = (): void => {
    if (this.props.totalAuthorizableTypes.length > MAX_AUTHORIZABLE_TYPE_TABS) {
      this.authorizableTypesForDropdown = this.props.totalAuthorizableTypes.slice(MAX_AUTHORIZABLE_TYPE_TABS - 1);
    }
  };

  /**
   * set the last tab to the given authorizable type
   * @param index index of an authorizable type in props.totalAuthorizableTypes
   */
  private setLastTab = (index: number): void => {
    this.authorizableTypesForTabs[MAX_AUTHORIZABLE_TYPE_TABS - 1] = this.props.totalAuthorizableTypes[index];
  };

  private getTabs = (): TabAndPanelContents[] => {
    let tabs = this.authorizableTypesForTabs.map((authorizableType: AuthorizableType, index: number): TabAndPanelContents => {
      // When the last tab is being used as a dropdown, it should always have a down arrow symbol before the authorizable type's name
      const addArrow = this.state.displayDropdown && !this.state.displayMore && index + 1 === MAX_AUTHORIZABLE_TYPE_TABS;
      const authorizableTypeName = getAuthorizableTypeLabel(authorizableType);
      const label = addArrow ? `▾ ${authorizableTypeName}` : authorizableTypeName;
      return {
        tabContent: { label },
        panelContent: null, // the panel content is handled outside of this component
      };
    });

    if (this.state.displayMore) {
      tabs.push({
        tabContent: { label: `▾ ${Messages.permissionsContentTabsOverflow()}`, },
        panelContent: null,
      });
    }

    return tabs;
  };

  private getDropdownValues = (): ListItem[] => {
    return this.authorizableTypesForDropdown.map((authorizableType: AuthorizableType): ListItem => {
      return {
        content: getAuthorizableTypeLabel(authorizableType),
        value: this.props.totalAuthorizableTypes.indexOf(authorizableType).toString(),
      };
    });
  };

  private handleTabIndexChanged = (index: number) => {
    const lastTabIndex = MAX_AUTHORIZABLE_TYPE_TABS - 1;
    if (this.state.displayDropdown) {
      if (index < lastTabIndex) {
        // if the dropdown tab is not on its original value, reset the tabs
        this.setOriginalTabs();
        this.setState({displayMore: true, selectedDropdownValue: null});
      } else if (index === lastTabIndex) {
        if (this.state.displayMore) {
          // open the dropdown
          this.tabsRef.current?.setActiveIndex(this.state.previousSelectedTab);
          return;
        }
        // select the authorizable type in the dropdown
        const selectedType = this.authorizableTypesForTabs[lastTabIndex];
        const value = this.props.totalAuthorizableTypes.indexOf(selectedType).toString();
        this.setState({displayMore: false, selectedDropdownValue: value});
      }
    }
    this.setState({previousSelectedTab: index,});
    const authorizableTypeIndex = this.props.totalAuthorizableTypes.indexOf(this.authorizableTypesForTabs[index]);
    this.props.onSelectAuthorizableType(authorizableTypeIndex);
  };

  /**
   * When an authorizable is selected from the dropdown, the selected authorizable becomes the last tab
   * and the dropdown values are updated to be any overflow authorizables. The dropdown closes and focus
   * is set on the new last tab.
   * @param value index of selected authorizable type in props.totalAuthorizableTypes
   */
  private handleDropdownValueSelected = (value: string) => {
    const selectedAuthorizableIndex = parseInt(value);
    this.setLastTab(selectedAuthorizableIndex);
    this.closeDropdown();
    this.setState({displayMore: false, selectedDropdownValue: value});
    this.props.onSelectAuthorizableType(selectedAuthorizableIndex);
    this.focusLastTab();
  };

  private focusLastTab = () => {
    // due to DropdownButton.props.focusOnClose = true, focus automatically returns to DropdownButton on dropdown close.
    // Timeout is needed so focusing on TabsWidget happens after the automatic DropdownButton focus.
    setTimeout(() => {
      this.tabsRef.current?.setActiveIndex(MAX_AUTHORIZABLE_TYPE_TABS - 1);
      if (this.tabsRef.current) {
        ReactDOM.findDOMNode(this.tabsRef.current).parentElement.click();
      }
    });
  };

  private handleSelectLastTab = (selectedElement: HTMLElement) => {
    // open the dropdown when the last tab is clicked
    // the last tab will include the down arrow symbol
    if (selectedElement.innerText.includes('▾')) {
      this.buttonRef.current?.toggle(true);
    }
  };

  private handleTabsKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const key = e.key;
    if (key === 'Enter' || key === ' ') {
      const target = e.target as HTMLElement;
      this.handleSelectLastTab(target);
    }
  };

  private handleTabsClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    this.handleSelectLastTab(target);
  };

  public render() {
    return (
      <div className={AuthorizableTypeSelectorStyles.Container}>
        <div className={AuthorizableTypeSelectorStyles.TabsContainer}>
          <TabsWidget
            activationMode={TabActivationMode.Manual}
            anchorTabIndex={0}
            initialActiveIndex={0}
            onActiveIndexChanged={this.handleTabIndexChanged}
            onClick={this.handleTabsClick}
            onKeyDownCapture={this.handleTabsKeyDown}
            tabs={this.getTabs()}
            testId={PERMISSIONS_TABS_TESTID}
            ref={this.tabsRef}
          />
        </div>
        {this.state.displayDropdown && <DropdownButton
          aria-controls={PERMISSIONS_TABS_OVERFLOW_POPUP_ID}
          aria-haspopup='menu'
          aria-label={Messages.permissionsContentTabsOverflow()}
          className={AuthorizableTypeSelectorStyles.TabsOverflowButton}
          focusOnClose={true /* needed to return focus to DropdownButton when dropdown is closed via esc key, otherwise focus would leave PermissionDialog */}
          id={PERMISSIONS_TABS_OVERFLOW_TESTID}
          label={undefined}
          onFocus={this.focusLastTab}
          onOpen={(done) => {this.closeDropdown = done;}}
          popupAlignment={'right'}
          popupContent={<List testId={PERMISSIONS_TABS_OVERFLOW_POPUP_ID} id={PERMISSIONS_TABS_OVERFLOW_POPUP_ID} items={this.getDropdownValues()} onSelect={this.handleDropdownValueSelected} selectedValue={this.state.selectedDropdownValue}/>}
          ref={this.buttonRef}
          tabIndex={-1 /* the dropdown button itself should appear invisible, including to screenreaders, as the last tab is acting as the dropdown button */}
          type={DropdownButtonType.Icon}
          />
        }
      </div>
    );
  }
}
